import { useState } from 'react';
import { Send, X, AlertTriangle, User, Mail, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { toast } from 'sonner';
import api from '../../../api/clients';

interface Cliente {
  id: number;
  nombre: string;
  email?: string;
  telefono?: string;
}

interface EnviarAvisoModalProps {
  cliente: Cliente | null;
  clientesSeleccionados?: Cliente[];
  onClose: () => void;
  onSuccess: () => void;
}

export function EnviarAvisoModal({ 
  cliente, 
  clientesSeleccionados = [], 
  onClose, 
  onSuccess 
}: EnviarAvisoModalProps) {
  const [loading, setLoading] = useState(false);
  const isBulk = clientesSeleccionados.length > 0;
  const destinatarios = isBulk ? clientesSeleccionados : (cliente ? [cliente] : []);
  const tieneEmail = destinatarios.some(c => c.email && c.email.trim() !== '');
  const TELEFONO_1 = import.meta.env.VITE_TELEFONO_1 || '11 1234-5678';
  const TELEFONO_2 = import.meta.env.VITE_TELEFONO_2 || '11 8765-4321';
  const EMAIL_OFICIAL = import.meta.env.VITE_EMAIL_OFICIAL || 'info@mitecnohogar.com.ar';
  const SITIO_WEB = import.meta.env.VITE_SITIO_WEB || 'www.mitecnohogar.com.ar';

  const [asunto, setAsunto] = useState(
    isBulk 
      ? ' Aviso importante - Seguridad en tus compras'
      : ` Aviso importante - Seguridad en tus compras`
  );

  const [mensaje, setMensaje] = useState(
    `Estimado cliente,\n\n` +
    `Le informamos sobre medidas importantes para su seguridad:\n\n` +
    `🔹 NUESTROS CANALES OFICIALES:\n` +
    `• Teléfonos de contacto: ${TELEFONO_1} / ${TELEFONO_2}\n` +
    `• Email oficial: ${EMAIL_OFICIAL}\n` +
    `• Sitio web: ${SITIO_WEB}\n\n` +
    `🔹 PREVENCIÓN DE FRAUDES:\n` +
    `• Nunca compartimos datos personales por teléfono.\n` +
    `• No realizamos cobros por fuera de nuestros canales oficiales.\n` +
    `• Ante cualquier duda, comuníquese directamente con nosotros.\n\n` +
    `🔹 RECOMENDACIONES:\n` +
    `• Verifique siempre que el número de teléfono sea el oficial.\n` +
    `• Desconfíe de ofertas demasiado buenas para ser verdad.\n` +
    `• Conserve siempre sus comprobantes de pago.\n\n` +
    `Ante cualquier consulta, no dude en contactarnos.\n\n` +
    `Saludos cordiales,\n` +
    `El equipo de Mi Tecno Hogar`
  );

  const handleEnviar = async () => {
    if (!asunto.trim() || !mensaje.trim()) {
      toast.error('Completa el asunto y el mensaje');
      return;
    }

    const clientesConEmail = destinatarios.filter(c => c.email && c.email.trim() !== '');
    if (clientesConEmail.length === 0) {
      toast.error('Ningún cliente seleccionado tiene email');
      return;
    }

    setLoading(true);
    try {
      // Enviar a todos los clientes seleccionados
      const emails = clientesConEmail.map(c => c.email);
      await api.post('/avisos/enviar', {
        emails,
        asunto,
        mensaje,
        clienteIds: clientesConEmail.map(c => c.id),
      });

      toast.success(`Aviso enviado a ${clientesConEmail.length} cliente(s)`);
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al enviar el aviso');
    } finally {
      setLoading(false);
    }
  };

  const totalDestinatarios = destinatarios.length;
  const destinatariosConEmail = destinatarios.filter(c => c.email && c.email.trim() !== '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-500" />
              Enviar Aviso
            </h2>
            <p className="text-sm text-gray-500">
              {isBulk 
                ? `A ${totalDestinatarios} clientes seleccionados`
                : cliente?.nombre || 'Envío individual'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-4">
          {/* Destinatarios */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destinatarios
            </label>
            <div className="bg-gray-50 rounded-lg p-3">
              {isBulk ? (
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-gray-500" />
                  <span className="text-sm text-gray-700">
                    {destinatariosConEmail.length} de {totalDestinatarios} con email
                  </span>
                  <span className="text-xs text-gray-400">
                    ({destinatariosConEmail.map(c => c.email).join(', ')})
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <User size={18} className="text-gray-500" />
                  <span className="text-sm text-gray-700">{cliente?.nombre}</span>
                  <Mail size={16} className="text-gray-400 ml-2" />
                  <span className="text-sm text-blue-600">{cliente?.email || 'Sin email'}</span>
                </div>
              )}
            </div>
            {!tieneEmail && (
              <p className="text-xs text-yellow-600 mt-1">
                Ningún cliente seleccionado tiene email registrado
              </p>
            )}
          </div>

          {/* Asunto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asunto *
            </label>
            <Input
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder="Asunto del aviso"
            />
          </div>

          {/* Mensaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensaje *
            </label>
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
              placeholder="Escribe el mensaje para los clientes..."
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setMensaje(prev => prev + '\n\n🔹 CANALES OFICIALES:\n• Teléfonos: ' + TELEFONO_1 + ' / ' + TELEFONO_2 + '\n• Email: ' + EMAIL_OFICIAL + '\n• Web: ' + SITIO_WEB);
                }}
                className="text-xs"
              >
                Agregar canales oficiales
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setMensaje(prev => prev + '\n\n🔹 PREVENCIÓN DE FRAUDES:\n• Nunca compartimos datos personales por teléfono.\n• No realizamos cobros por fuera de nuestros canales oficiales.');
                }}
                className="text-xs"
              >
                Agregar prevención
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setMensaje(prev => prev + '\n\n🔹 RECOMENDACIONES:\n• Verifique siempre los números de teléfono oficiales.\n• Desconfíe de ofertas demasiado buenas.\n• Conserve sus comprobantes de pago.');
                }}
                className="text-xs"
              >
                Agregar recomendaciones
              </Button>
            </div>
          </div>

          {/* Advertencia */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-3">
            <AlertTriangle size={18} className="text-yellow-600 mt-0.5 shrink-0" />
            <div className="text-xs text-yellow-700">
              <p className="font-medium">Importante:</p>
              <p>Este aviso no es spam. Se utiliza para informar sobre medidas de seguridad, prevención de fraudes y comunicar los canales oficiales de contacto.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex gap-3">
          <Button
            onClick={handleEnviar}
            disabled={loading || !tieneEmail}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                Enviando...
              </span>
            ) : (
              <>
                <Send size={18} />
                Enviar Aviso {isBulk ? `(${destinatariosConEmail.length})` : ''}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}